import { Clickable } from '@carvana/merch-components';
import isEqual from 'lodash.isequal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { COOKIES } from '~/constants';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import SavedSearchesContext from '~/contexts/SavedSearchesContext';
import StaticContext from '~/contexts/StaticContext';
import usePriceDrop from '~/hooks/usePriceDrop';
import Close from '~/icons/Close';
import Wave from '~/icons/Wave';
import { SearchDispatcherEvent } from '~/native/dispatchers/constants';
import sendMessageToNative from '~/native/dispatchers/sendMessageToNative';
import BrowserCookies from '~/utilities/BrowserCookies';
import decodeCvnaId from '~/utilities/cvnaId';
import NewPriceIconLarge from './NewPriceIconLarge';
import NewPriceSaveSearch from './NewPriceSaveSearch';

enum DismissedNewPrice {
  None = 0,
  Banner = 1,
  SaveSearch = 2
}

const NewPriceTagId = '103';
const NewPriceFilter = { tagIds: [NewPriceTagId] };

function setBannerDismissedCookie(value: DismissedNewPrice) {
  const fourteenDays = 14 * 24 * 60 * 60 * 1000;
  const expires = new Date(Date.now() + fourteenDays);

  BrowserCookies.set(COOKIES.dismissedNewPriceBanner, String(value), {
    expires,
    path: '/'
  });
}

function NewPriceBanner() {
  const {
    appliedFilters: { tagIds },
    setAppliedFilters
  } = useContext(AppliedFiltersContext);
  const { savedSearches } = useContext(SavedSearchesContext);
  const { dismissedNewPrice } = useContext(StaticContext);
  const { bannerText, displayText } = usePriceDrop();
  const [localDismissedNewPrice, setLocalDismissedNewPrice] =
    useState<DismissedNewPrice>(dismissedNewPrice);

  const setBannerDismissed = useCallback(dismissed => {
    sendMessageToNative(SearchDispatcherEvent.DismissPriceDrop, {
      value: String(dismissed)
    });
    setBannerDismissedCookie(dismissed);
    setLocalDismissedNewPrice(dismissed);
  }, []);

  const showNewPriceBanner = localDismissedNewPrice < DismissedNewPrice.Banner;
  const showSaveSearchBanner =
    localDismissedNewPrice < DismissedNewPrice.SaveSearch;

  const hasNewPriceFilter = useMemo(
    () => tagIds?.includes(NewPriceTagId) || false,
    [tagIds]
  );

  useEffect(() => {
    // Dismiss the banner if the user has a saved search with the new price filter
    const hasNewPriceSavedSearch = savedSearches.some(savedSearch =>
      isEqual(decodeCvnaId(savedSearch.url), NewPriceFilter)
    );
    if (hasNewPriceSavedSearch) {
      setBannerDismissed(DismissedNewPrice.SaveSearch);
    }
  }, [savedSearches, setBannerDismissed]);

  useEffect(() => {
    const shouldDismissNewPriceBanner =
      hasNewPriceFilter && localDismissedNewPrice === DismissedNewPrice.None;

    if (shouldDismissNewPriceBanner) {
      setBannerDismissed(DismissedNewPrice.Banner);
    }
  }, [localDismissedNewPrice, hasNewPriceFilter, setBannerDismissed]);

  if (hasNewPriceFilter && showSaveSearchBanner) {
    return (
      <NewPriceSaveSearch
        onClose={() => {
          setBannerDismissed(DismissedNewPrice.SaveSearch);
        }}
      />
    );
  }

  return showNewPriceBanner ? (
    <div
      className="bg-white border border-grey-2 mb-16 overflow-hidden relative rounded-md w-full z-[0] m:mb-32"
      data-qa="price-drop-banner"
    >
      <Wave className="-translate-x-1/2 -translate-y-1/2 -z-10 absolute inset-1/2 min-h-full min-w-full m:-translate-y-[45%]" />
      <div className="flex gap-24 h-full items-center p-16 m:p-32">
        <div className="align-start flex flex-col gap-8 justify-center m:gap-16">
          <div
            className="bg-green-2 px-16 py-[2px] rounded t-header-xs text-white w-fit"
            data-qa="new-price-badge"
          >
            {displayText}
          </div>
          <div className="flex flex-col gap-8">
            <span className="t-header-xs text-blue-6 m:t-header-m">
              {bannerText}
            </span>
            <Clickable
              className="t-link-s text-blue-6 m:t-link-m"
              data-analytics-id="New Price - Show All New Prices"
              data-qa="show-all-new-prices"
              onClick={() => setAppliedFilters(NewPriceFilter)}
            >
              Show All {displayText}s
            </Clickable>
          </div>
        </div>
        <div className="flex flex-auto justify-end">
          <div className="-mr-72 flex items-center relative m:mr-0">
            <img
              alt="New Price Vehicle"
              className="w-[200px] z-0 m:w-[250px]"
              src={`${process.env.CVNA_APP_ASSETS_CDN}/search-assets/new-price-vehicle.png?width=250&trim=40,0,40,0`}
            />
            <NewPriceIconLarge className="-top-12 absolute h-[42px] left-36 w-[42px] z-[-1] m:-top-8 m:h-[62px] m:left-auto m:right-4 m:w-[62px] m:z-[1]" />
          </div>
        </div>
      </div>
      <Clickable
        data-analytics-id="New Price - Close Banner"
        data-qa="close-price-drop-banner"
        onClick={() => setBannerDismissed(DismissedNewPrice.Banner)}
        className="absolute flex h-[36px] items-center justify-center right-4 top-4 w-[36px] z-10"
      >
        <Close className="fill-grey-5" width={'16px'} height={'16px'} />
      </Clickable>
    </div>
  ) : null;
}

export default NewPriceBanner;
